import { useEffect, useMemo, useState } from "react";
import { RubricGuide, isPbgs } from "@/components/testsessions/Pbgs";
import GridRow, { rowState } from "@/components/testsessions/GridRow";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_REASONS = ["Falsa partenza", "Caduta o perdita di equilibrio", "Percorso non completato", "Interruzione del gesto", "Uscita dalla pedana/corsia", "Errore di cronometraggio", "Altro"];

export const testProgress = (s, test) => {
  const planned = s.planned_attempts[test.id];
  const c = { complete: 0, partial: 0, missing: 0, absent: 0 };
  s.participants.filter((p) => p.status !== "removed").forEach((p) => {
    const r = s.results.find((x) => x.test_id === test.id && x.athlete_id === p.athlete_id);
    c[rowState(r, planned, p.status === "absent")] += 1;
  });
  return c;
};

export default function SessionGrid({ session: s, test, locked, onSaved, onPending }) {
  const planned = s.planned_attempts[test.id];
  const [status, setStatus] = useState({});
  const reasons = useMemo(() => {
    const fromTest = (test.invalidity_criteria || "").split(/[;.]/).map((x) => x.trim()).filter(Boolean).map((x) => x[0].toUpperCase() + x.slice(1));
    return Array.from(new Set([...fromTest, ...DEFAULT_REASONS]));
  }, [test]);
  const rows = s.participants.filter((p) => p.status !== "removed");
  const onStatus = (aid, st) => setStatus((m) => (m[aid] === st ? m : { ...m, [aid]: st }));
  const pending = Object.values(status).filter((x) => ["dirty", "saving", "error", "stale", "incomplete"].includes(x)).length;
  useEffect(() => { onPending(test.id, pending); }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (aid, attempts, rev) => {
    try {
      const { data } = await api.put(`/test-sessions/${s.id}/results`, { test_id: test.id, athlete_id: aid, attempts, rev });
      onSaved(test.id, aid, data.result);
      return data.result;
    } catch (e) {
      const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Errore di salvataggio"); throw e;
    }
  };

  return (
    <div className="space-y-2" data-testid={`grid-${test.id}`}>
      <div className="flex items-center gap-3 text-xs text-zinc-600 flex-wrap">
        <span>{planned} tentativi previsti · {test.unit}{test.fencing_technical ? (isPbgs(test) ? " · tempo + criteri P/B/G/S (✓ ✗ non valutato) + voto 1–5 del Maestro" : " · tempo + 4 criteri (S sequenza piedi, G assetto di guardia, C continuità, T gesto specifico) → voto 1–5") : " · solo tempo, nessun voto schermistico"}</span>
        <span data-testid={`grid-pending-${test.id}`} className={`ml-auto px-2 py-0.5 rounded ${pending ? "bg-amber-100 text-amber-900" : "bg-emerald-50 text-emerald-800"}`}>{pending ? `${pending} righe non salvate` : "Tutto salvato"}</span>
      </div>
      {test.fencing_technical && isPbgs(test) && <RubricGuide test={test} />}
      <div className="max-h-[70vh] overflow-auto border border-zinc-200 rounded-md bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-20 bg-zinc-50 text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Atleta</th>
              {Array.from({ length: planned }, (_, i) => <th key={i} className="sticky top-0 z-10 bg-zinc-50 text-left px-2 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Tentativo {i + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((p, idx) => (
              <GridRow key={p.athlete_id} rowIdx={idx} p={p} test={test} planned={planned} absent={p.status === "absent"} locked={locked} reasons={reasons} onStatus={onStatus}
                result={s.results.find((x) => x.test_id === test.id && x.athlete_id === p.athlete_id)} onSave={save} />
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="p-6 text-sm text-zinc-500">Nessun partecipante: aggiungili nella scheda Partecipanti.</div>}
      </div>
    </div>
  );
}
